/**
 * useMaxInstallments — reads the admin-editable "parcelas máximas sem juros"
 * setting from GET /api/settings/installments and caches it at module level.
 *
 * The value is fetched exactly ONCE per page load, regardless of how many
 * ProductCards call the hook (a listing page can render 30+ cards). Every
 * subscriber updates when the promise resolves.
 */
import { useEffect, useState } from "react";
import { api } from "./api";

let cachedValue = null;                 // number | null
let pendingRequest = null;              // Promise<number> | null
const subscribers = new Set();          // setState fns of every mounted hook

function fetchOnce(fallback) {
  if (cachedValue !== null) return Promise.resolve(cachedValue);
  if (pendingRequest) return pendingRequest;
  pendingRequest = api
    .get("/settings/installments")
    .then((r) => {
      const n = Number(r?.data?.max);
      cachedValue = Number.isFinite(n) && n >= 1 ? n : fallback;
      subscribers.forEach((fn) => fn(cachedValue));
      return cachedValue;
    })
    .catch(() => {
      // Never break the render — fall back to a reasonable default and let
      // the next page load try again.
      cachedValue = fallback;
      subscribers.forEach((fn) => fn(cachedValue));
      return cachedValue;
    })
    .finally(() => {
      pendingRequest = null;
    });
  return pendingRequest;
}

export function useMaxInstallments(fallback = 6) {
  const [n, setN] = useState(cachedValue ?? fallback);
  useEffect(() => {
    subscribers.add(setN);
    if (cachedValue === null) fetchOnce(fallback);
    else setN(cachedValue);
    return () => { subscribers.delete(setN); };
  }, [fallback]);
  return n;
}

/**
 * Invalidate the cache after the admin saves a new value. Called from the
 * settings page so any card/PDP mounted in the same session picks up the new
 * number without a full reload.
 */
export function refreshInstallmentsSetting() {
  cachedValue = null;
  pendingRequest = null;
  fetchOnce(6);
}
